import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import useTimePicker from './useTimePicker';

/**
 * Compute popup position synchronously from the anchor element's bounding rect.
 * Using `fixed` positioning so it works correctly regardless of scroll.
 * @param {HTMLElement|null} anchorEl
 * @returns {React.CSSProperties}
 */
const getPopupStyle = (anchorEl) => {
  if (!anchorEl) return { position: 'fixed', top: 0, left: 0, zIndex: 1000 };
  const rect = anchorEl.getBoundingClientRect();
  return {
    position: 'fixed',
    top: rect.bottom,
    left: rect.left,
    zIndex: 1000,
  };
};

// ─── sub-components ──────────────────────────────────────────────────────────

const SpinnerCol = memo(({ label, displayValue, onIncrement, onDecrement }) => (
  <div className={`p-${label}-picker`}>
    <button type="button" className="p-link" onClick={onIncrement}>
      <span className="pi pi-chevron-up" />
    </button>
    <span>{displayValue}</span>
    <button type="button" className="p-link" onClick={onDecrement}>
      <span className="pi pi-chevron-down" />
    </button>
  </div>
));

SpinnerCol.displayName = 'SpinnerCol';

const Separator = () => (
  <div className="p-separator">
    <span>:</span>
  </div>
);

// ─── popup ───────────────────────────────────────────────────────────────────

const TimePopup = memo(({ anchorEl, style: initialStyle, popupRef, h12, minutes, ampm, onIncH, onDecH, onIncM, onDecM, onToggleAmPm }) => {
  const [style, setStyle] = useState(initialStyle);

  // Recompute position on resize / scroll (handles virtual keyboard on mobile)
  useEffect(() => {
    const reposition = () => {
      if (!anchorEl) return;
      const rect = anchorEl.getBoundingClientRect();
      setStyle({ position: 'fixed', top: rect.bottom, left: rect.left, zIndex: 1000 });
    };
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [anchorEl]);

  const mm = String(minutes).padStart(2, '0');
  const hh = String(h12).padStart(2, '0');

  return ReactDOM.createPortal(
    <div ref={popupRef} className="p-datepicker p-datepicker-timeonly p-component" style={style}>
      <div className="p-timepicker">
        <SpinnerCol label="hour" displayValue={hh} onIncrement={onIncH} onDecrement={onDecH} />
        <Separator />
        <SpinnerCol label="minute" displayValue={mm} onIncrement={onIncM} onDecrement={onDecM} />
        <Separator />
        <div className="p-ampm-picker">
          <button type="button" className="p-link" onClick={onToggleAmPm}>
            <span className="pi pi-chevron-up" />
          </button>
          <span>{ampm}</span>
          <button type="button" className="p-link" onClick={onToggleAmPm}>
            <span className="pi pi-chevron-down" />
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
});

TimePopup.displayName = 'TimePopup';

// ─── main component ───────────────────────────────────────────────────────────

/**
 * Custom time-only picker that replicates PrimeReact Calendar's timeOnly UI
 * using the same CSS class names (so the saga-blue theme applies automatically)
 * while providing clean control over 24-hour input parsing and 30-minute
 * boundary snapping.
 *
 * Accepted props (mirrors the subset used by consumers):
 *   value       {Date|null}  - Current selected time (browser-local Date).
 *   onChange    {Function}   - Called with { value: Date, target: { value: Date } }.
 *   placeholder {string}
 *   className   {string}     - Applied to the outer <span> wrapper.
 *   id          {string}     - Forwarded to the hidden input for a11y.
 *   inputRef    {ref|fn}     - Forwarded to the <input> element.
 *   disabled    {boolean}
 */
const CustomTimePicker = memo(({ value, onChange, placeholder, className, id, inputRef, disabled }) => {
  const anchorRef = useRef(null);

  const {
    h12,
    minutes,
    ampm,
    isOpen,
    wrapperRef,
    popupRef,
    incrementHour,
    decrementHour,
    incrementMinute,
    decrementMinute,
    toggleAmPm,
    handleInputBlur,
    togglePopup,
    formattedValue,
  } = useTimePicker({ value, onChange, id });

  // Local state for the raw text the user is typing.
  // Stays in sync with formattedValue unless the user is mid-type.
  const [inputText, setInputText] = useState(formattedValue);
  const isEditingRef = useRef(false);

  // Keep input text in sync when the value prop changes externally
  // (e.g. spinner buttons emit a new value → parent updates → prop changes)
  useEffect(() => {
    if (!isEditingRef.current) {
      setInputText(formattedValue);
    }
  }, [formattedValue]);

  const handleChange = useCallback((e) => {
    isEditingRef.current = true;
    setInputText(e.target.value);
  }, []);

  const handleBlur = useCallback(
    (e) => {
      const raw = e.target.value;
      const success = handleInputBlur(raw);
      isEditingRef.current = false;
      // If parsing failed, revert the displayed text to the last good value
      if (!success) {
        setInputText(formattedValue);
      }
    },
    [handleInputBlur, formattedValue]
  );

  const handleInputClick = useCallback(() => {
    if (!disabled) togglePopup();
  }, [disabled, togglePopup]);

  // Merge the consumer's inputRef with our anchorRef
  const setInputRef = useCallback(
    (node) => {
      anchorRef.current = node;
      if (!inputRef) return;
      if (typeof inputRef === 'function') inputRef(node);
      else inputRef.current = node;
    },
    [inputRef]
  );

  return (
    <span
      ref={wrapperRef}
      className={`p-calendar p-component p-inputwrapper${isOpen ? ' p-inputwrapper-focus' : ''}${className ? ` ${className}` : ''}`}
    >
      <input
        ref={setInputRef}
        id={id}
        type="text"
        className="p-inputtext p-component"
        value={inputText}
        placeholder={placeholder}
        disabled={disabled}
        onChange={handleChange}
        onBlur={handleBlur}
        onClick={handleInputClick}
        autoComplete="off"
      />

      {isOpen && (
        <TimePopup
          anchorEl={anchorRef.current}
          style={getPopupStyle(anchorRef.current)}
          popupRef={popupRef}
          h12={h12}
          minutes={minutes}
          ampm={ampm}
          onIncH={incrementHour}
          onDecH={decrementHour}
          onIncM={incrementMinute}
          onDecM={decrementMinute}
          onToggleAmPm={toggleAmPm}
        />
      )}
    </span>
  );
});

CustomTimePicker.displayName = 'CustomTimePicker';

export default CustomTimePicker;
